/**
 * Utility helpers for calculating grid layouts in the budgeting game.
 *
 * `bestSquareGrid(n)` returns a `{ cols, rows }` configuration that keeps the
 * grid as square as possible given `n` items.  The algorithm favours square
 * shapes, fewer empty cells and larger cell size just like the previous
 * heuristic that lived in BudgetScene.
 */
export interface GridDims {
  cols: number;
  rows: number;
}

const GAP = 0; // px gap between cat cells to avoid overlap

/**
 * Choose an optimal (cols, rows) for `n` cells to approximate a square grid.
 * – Minimises |cols-rows| (squareness) first
 * – Minimises empty cells second
 * – Maximises cell size (implicitly by preferring fewer cols) last
 */
export function bestSquareGrid(n: number): GridDims {
  if (n <= 0) return { cols: 0, rows: 0 };

  let bestCols = 1;
  let bestScore = Number.POSITIVE_INFINITY;

  const MAX_COLS = n; // worst-case single row

  for (let cols = 1; cols <= MAX_COLS; cols++) {
    const rows = Math.ceil(n / cols);
    const sqDiff = Math.abs(cols - rows); // squareness
    const empty = rows * cols - n; // wasted cells

    // Bias towards layouts with fewer rows (which spreads cats horizontally)
    const score = sqDiff * 1000 + empty * 10 + rows; // smaller rows preferred

    if (score < bestScore) {
      bestScore = score;
      bestCols = cols;
    }
  }

  return { cols: bestCols, rows: Math.ceil(n / bestCols) };
}

import { CellBounds, CategoryData } from "../config/types";

/**
 * Calculate pixel-perfect cell bounds for a responsive grid given the
 * available view size and desired item count.
 *
 * The algorithm mirrors the heuristic previously embedded in
 * BudgetSceneReact / BudgetGrid and prefers layouts that are:
 *   1) As square as possible (|cols-rows| minimal)
 *   2) Contain as few empty cells as possible
 *   3) Provide the largest individual cell size
 */
export function buildCellBounds(
  viewW: number,
  viewH: number,
  n: number,
): CellBounds[] {
  if (n <= 0) return [];

  const MIN_CELL_W = 100;
  const maxColsByWidth = Math.max(1, Math.floor(viewW / MIN_CELL_W));
  const limCols = Math.min(maxColsByWidth, n);

  let bestCols = 1,
    bestScore = Infinity;

  for (let cols = 1; cols <= limCols; cols++) {
    const rows = Math.ceil(n / cols);
    const sqDiff = Math.abs(cols - rows);
    const empty = rows * cols - n;
    const cell = Math.min(viewW / cols, viewH / rows);
    const score = sqDiff * 1000 + empty * 10 - cell;

    if (score < bestScore) {
      bestScore = score;
      bestCols = cols;
    }
  }

  const cols = bestCols;
  const rows = Math.ceil(n / cols);
  const cellW = viewW / cols;
  const cellH = viewH / rows;

  // Center the grid vertically – keep consistent with old implementation
  const gridHeight = rows * cellH;
  const gridOffsetY = Math.max(0, (viewH - gridHeight) / 2);

  return Array.from({ length: n }, (_, i) => {
    const row = Math.floor(i / cols);
    const colInRow = i % cols;

    const itemsInRow = row === rows - 1 ? n - (rows - 1) * cols : cols;
    const leftoverSpace = viewW - itemsInRow * cellW;
    const rowOffsetX = leftoverSpace / 2;

    const x = rowOffsetX + colInRow * cellW;
    const y = gridOffsetY + row * cellH;
    return { x, y, w: cellW, h: cellH } as CellBounds;
  });
}

/**
 * Interface for XY coordinate plane layout
 */
export interface CoordinatePlaneBounds extends CellBounds {
  /** The surplus/deficit value for this category (X-axis position) */
  surplus: number;
  /** Whether this category is necessary (Y-axis position) */
  necessary: boolean;
  /** The row index for this category (each cat gets its own row) */
  rowIndex: number;
}

/**
 * Quadrant class to handle positioning of cats within a specific quadrant
 */
export class Quadrant {
  constructor(
    public x: number,
    public y: number,
    public width: number,
    public height: number,
    public name: string,
  ) {}

  /**
   * Position cats within this quadrant using optimal sizing
   */
  positionCats(
    cats: CategoryData[],
    allCategories: CategoryData[],
  ): CoordinatePlaneBounds[] {
    if (cats.length === 0) return [];

    // Sort cats by absolute surplus/deficit value (closer to zero = closer to center)
    const sortedCats = [...cats].sort((a, b) => {
      const surplusA = Math.abs(a.budgeted - a.spent);
      const surplusB = Math.abs(b.budgeted - b.spent);
      return surplusA - surplusB; // Ascending order (smaller differentials first, closer to center)
    });

    // Calculate optimal grid dimensions to maximize cat size
    const gridDims = bestSquareGrid(sortedCats.length);

    // Calculate the largest possible cell size that fits in the quadrant *after* reserving gaps
    const maxCellWidth =
      (this.width - GAP * (gridDims.cols - 1)) / gridDims.cols;
    const maxCellHeight =
      (this.height - GAP * (gridDims.rows - 1)) / gridDims.rows;

    // Use the smaller dimension to maintain aspect ratio, but ensure minimum size
    const cellSize = Math.min(maxCellWidth, maxCellHeight);
    const cellWidth = cellSize;
    const cellHeight = cellSize;

    return sortedCats.map((category, index) => {
      const row = Math.floor(index / gridDims.cols);
      const col = index % gridDims.cols;

      // Center the grid within the quadrant including gaps
      const totalGridWidth =
        gridDims.cols * cellWidth + (gridDims.cols - 1) * GAP;
      const totalGridHeight =
        gridDims.rows * cellHeight + (gridDims.rows - 1) * GAP;
      const offsetX = (this.width - totalGridWidth) / 2;
      const offsetY = (this.height - totalGridHeight) / 2;

      const x = this.x + offsetX + col * (cellWidth + GAP);
      const y = this.y + offsetY + row * (cellHeight + GAP);
      const surplus = category.budgeted - category.spent;

      return {
        x,
        y,
        w: cellWidth,
        h: cellHeight,
        surplus,
        necessary: category.necessary,
        rowIndex: allCategories.findIndex((cat) => cat.name === category.name),
      } as CoordinatePlaneBounds;
    });
  }

  /**
   * Position cats within this quadrant using a specified cell size
   */
  positionCatsWithSize(
    cats: CategoryData[],
    allCategories: CategoryData[],
    cellSize: number,
  ): CoordinatePlaneBounds[] {
    if (cats.length === 0) return [];

    // Sort cats by absolute surplus/deficit value (closer to zero = closer to center)
    const sortedCats = [...cats].sort((a, b) => {
      const surplusA = Math.abs(a.budgeted - a.spent);
      const surplusB = Math.abs(b.budgeted - b.spent);
      return surplusA - surplusB; // Ascending order (smaller differentials first, closer to center)
    });

    // Calculate grid dimensions for this specific number of cats
    const gridDims = bestSquareGrid(sortedCats.length);

    // Ensure the provided cellSize plus gaps fit in the quadrant
    const maxAllowedWidth =
      (this.width - GAP * (gridDims.cols - 1)) / gridDims.cols;
    const maxAllowedHeight =
      (this.height - GAP * (gridDims.rows - 1)) / gridDims.rows;

    const adjustedCellSize = Math.min(
      cellSize,
      maxAllowedWidth,
      maxAllowedHeight,
    );
    const cellWidth = adjustedCellSize;
    const cellHeight = adjustedCellSize;

    return sortedCats.map((category, index) => {
      const row = Math.floor(index / gridDims.cols);
      const col = index % gridDims.cols;

      // Center the grid within the quadrant including gaps
      const totalGridWidth =
        gridDims.cols * cellWidth + (gridDims.cols - 1) * GAP;
      const totalGridHeight =
        gridDims.rows * cellHeight + (gridDims.rows - 1) * GAP;
      const offsetX = (this.width - totalGridWidth) / 2;
      const offsetY = (this.height - totalGridHeight) / 2;

      const x = this.x + offsetX + col * (cellWidth + GAP);
      const y = this.y + offsetY + row * (cellHeight + GAP);
      const surplus = category.budgeted - category.spent;

      return {
        x,
        y,
        w: cellWidth,
        h: cellHeight,
        surplus,
        necessary: category.necessary,
        rowIndex: allCategories.findIndex((cat) => cat.name === category.name),
      } as CoordinatePlaneBounds;
    });
  }
}

/**
 * Calculate bounds for a quadrant-based layout with proper positioning
 */
export function buildCoordinatePlaneBounds(
  viewW: number,
  viewH: number,
  categories: CategoryData[],
  levelPrecision: number = 100,
  fixedBounds?: { minSurplus: number; maxSurplus: number },
): CoordinatePlaneBounds[] {
  if (categories.length === 0) return [];

  const gridPadding = 40;
  const availableWidth = viewW - gridPadding * 2;
  const availableHeight = viewH - gridPadding * 2;

  const quadrantWidth = availableWidth / 2;
  const quadrantHeight = availableHeight / 2;

  // Create the four quadrants
  const topLeft = new Quadrant(
    gridPadding,
    gridPadding,
    quadrantWidth,
    quadrantHeight,
    "Deficit/Necessary",
  );

  const topRight = new Quadrant(
    gridPadding + quadrantWidth,
    gridPadding,
    quadrantWidth,
    quadrantHeight,
    "Surplus/Necessary",
  );

  const bottomLeft = new Quadrant(
    gridPadding,
    gridPadding + quadrantHeight,
    quadrantWidth,
    quadrantHeight,
    "Deficit/Discretionary",
  );

  const bottomRight = new Quadrant(
    gridPadding + quadrantWidth,
    gridPadding + quadrantHeight,
    quadrantWidth,
    quadrantHeight,
    "Surplus/Discretionary",
  );

  // Separate categories into quadrants
  const necessarySurplus = categories.filter(
    (cat) => cat.necessary && cat.budgeted - cat.spent > 0,
  );
  const necessaryDeficit = categories.filter(
    (cat) => cat.necessary && cat.budgeted - cat.spent < 0,
  );
  const discretionarySurplus = categories.filter(
    (cat) => !cat.necessary && cat.budgeted - cat.spent > 0,
  );
  const discretionaryDeficit = categories.filter(
    (cat) => !cat.necessary && cat.budgeted - cat.spent < 0,
  );
  const balancedCats = categories.filter(
    (cat) => cat.budgeted - cat.spent === 0,
  );

  // Put balanced cats on the surplus side
  const necessaryBalanced = balancedCats.filter((cat) => cat.necessary);
  const discretionaryBalanced = balancedCats.filter((cat) => !cat.necessary);

  // Add balanced necessary cats to the surplus side
  if (necessaryBalanced.length > 0) {
    necessarySurplus.push(...necessaryBalanced);
  }

  // Add balanced discretionary cats to the surplus side
  if (discretionaryBalanced.length > 0) {
    discretionarySurplus.push(...discretionaryBalanced);
  }

  // Calculate consistent cat size across all quadrants
  const allQuadrantCats = [
    { cats: necessaryDeficit, quadrant: topLeft },
    { cats: necessarySurplus, quadrant: topRight },
    { cats: discretionaryDeficit, quadrant: bottomLeft },
    { cats: discretionarySurplus, quadrant: bottomRight },
  ];

  // Find the maximum number of cats in any quadrant
  const maxCatsInQuadrant = Math.max(
    ...allQuadrantCats.map((q) => q.cats.length),
  );

  // Calculate optimal grid dimensions for the worst case (most cats)
  const gridDims = bestSquareGrid(maxCatsInQuadrant);

  // Calculate the largest possible cell size that would fit in any quadrant
  const maxCellWidth = quadrantWidth / gridDims.cols;
  const maxCellHeight = quadrantHeight / gridDims.rows;

  // Use the smaller dimension to maintain aspect ratio (no hard cap)
  const cellSize = Math.min(maxCellWidth, maxCellHeight);

  // Position cats in each quadrant using consistent sizing
  const allPositions = [
    ...topLeft.positionCatsWithSize(necessaryDeficit, categories, cellSize),
    ...topRight.positionCatsWithSize(necessarySurplus, categories, cellSize),
    ...bottomLeft.positionCatsWithSize(
      discretionaryDeficit,
      categories,
      cellSize,
    ),
    ...bottomRight.positionCatsWithSize(
      discretionarySurplus,
      categories,
      cellSize,
    ),
  ];

  // Create a properly indexed array that matches the original category order
  const indexedPositions = new Array(categories.length);
  allPositions.forEach((pos) => {
    indexedPositions[pos.rowIndex] = pos;
  });

  return indexedPositions;
}
